import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Link2, Search } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";
import VehicleTabsNav from "../components/VehicleTabsNav";

export default function Remorci() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trailers, setTrailers] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);

  const canManage = ["admin", "planificator", "magazioner"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/trailers"),
        api.get("/cars").catch(() => ({ data: [] })),
      ]);
      setTrailers(t.data || []);
      setCars(c.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const carById = useMemo(() => {
    const m = {};
    for (const c of cars) m[c.id] = c;
    return m;
  }, [cars]);

  const filtered = useMemo(() => {
    return trailers.filter((t) => {
      if (!showInactive && t.active === false) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        (t.numar_inmatriculare || "").toLowerCase().includes(s) ||
        (t.marca || "").toLowerCase().includes(s) ||
        (t.model || "").toLowerCase().includes(s) ||
        (t.vin || "").toLowerCase().includes(s) ||
        (t.intern_number || "").toLowerCase().includes(s)
      );
    });
  }, [trailers, q, showInactive]);

  const del = async (tid) => {
    if (!(await confirm({ body: "Sigur ștergi această remorcă?", danger: true }))) return;
    try {
      await api.delete(`/trailers/${tid}`);
      toast.success("Remorcă ștearsă");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  return (
    <div className="space-y-5" data-testid="remorci-page">
      <VehicleTabsNav />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Truck className="text-amber-700" size={22}/>
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Remorci</h1>
            <div className="text-xs text-zinc-500">Evidență remorci · asociere cu autoturisme · documente și poze</div>
          </div>
        </div>
        {canManage && (
          <Button onClick={() => setCreating(true)} className="bg-[var(--primary)]" data-testid="add-trailer-btn">
            <Plus size={16} className="mr-1"/> Adaugă remorcă
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="card-flat p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută după înmatriculare, marcă, model, VIN..."
            className="pl-9 h-9"
            data-testid="remorci-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="remorci-include-inactive"/>
          Arată și inactive
        </label>
        <div className="text-xs text-zinc-500 ml-auto">
          {filtered.length} {filtered.length === 1 ? "remorcă" : "remorci"}
        </div>
      </div>

      {loading ? (
        <div className="card-flat p-8 text-center text-zinc-400">Se încarcă…</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-10 text-center">
          <Truck size={40} className="mx-auto text-zinc-300 mb-3"/>
          <div className="text-sm text-zinc-500">
            {q ? "Nicio remorcă pentru această căutare." : "Nicio remorcă înregistrată încă."}
          </div>
          {canManage && !q && (
            <Button onClick={() => setCreating(true)} className="mt-4 bg-[var(--primary)]">
              <Plus size={16} className="mr-1"/> Adaugă prima remorcă
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="remorci-grid">
          {filtered.map((t) => {
            const linked = t.linked_car_id ? carById[t.linked_car_id] : null;
            return (
              <div
                key={t.id}
                onClick={() => navigate(`/remorci/${t.id}`)}
                className={`card-flat p-4 cursor-pointer hover:border-amber-300 transition-colors relative group ${t.active === false ? "opacity-60" : ""}`}
                data-testid={`trailer-card-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                    <Truck className="text-amber-700" size={20}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-bold leading-tight truncate">
                        {t.numar_inmatriculare || "— fără nr. —"}
                      </div>
                      {t.intern_number && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-bold">#{t.intern_number}</span>
                      )}
                      {t.active === false && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">INACTIVĂ</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-600 mt-0.5 truncate">
                      {[t.marca, t.model, t.an_fabricatie].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {t.vin && (
                      <div className="text-[10px] text-zinc-400 mt-1 font-mono truncate" title={t.vin}>VIN: {t.vin}</div>
                    )}
                    {linked ? (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                        <Link2 size={11}/>
                        <span className="font-semibold truncate">Atașată la {linked.numar_inmatriculare || linked.marca}</span>
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-zinc-400 italic">Neatașată</div>
                    )}
                  </div>
                </div>

                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); del(t.id); }}
                    className="absolute top-2 right-2 p-1.5 rounded hover:bg-red-50 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Șterge"
                    data-testid={`trailer-delete-${t.id}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <TrailerFormDialog
          cars={cars}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}


function TrailerFormDialog({ trailer = null, cars = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    numar_inmatriculare: trailer?.numar_inmatriculare || "",
    intern_number: trailer?.intern_number || "",
    marca: trailer?.marca || "",
    model: trailer?.model || "",
    an_fabricatie: trailer?.an_fabricatie || "",
    vin: trailer?.vin || "",
    culoare: trailer?.culoare || "",
    capacitate_kg: trailer?.capacitate_kg || "",
    notes: trailer?.notes || "",
    active: trailer?.active ?? true,
    linked_car_id: trailer?.linked_car_id || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.numar_inmatriculare && !form.marca) {
      toast.warning("Introdu cel puțin numărul de înmatriculare sau marca");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        an_fabricatie: form.an_fabricatie ? parseInt(form.an_fabricatie, 10) : null,
        capacitate_kg: form.capacitate_kg ? parseFloat(form.capacitate_kg) : null,
        linked_car_id: form.linked_car_id || null,
      };
      if (trailer?.id) {
        await api.put(`/trailers/${trailer.id}`, payload);
        toast.success("Remorcă actualizată");
      } else {
        await api.post("/trailers", payload);
        toast.success("Remorcă adăugată");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-lg" data-testid="trailer-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={18} className="text-amber-700"/>
            {trailer ? "Editează remorcă" : "Remorcă nouă"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="small-label">Nr. înmatriculare</Label>
            <Input value={form.numar_inmatriculare} onChange={(e) => setForm({ ...form, numar_inmatriculare: e.target.value })} placeholder="ZH-12345" data-testid="trailer-inmatriculare"/>
          </div>
          <div>
            <Label className="small-label">Nr. intern</Label>
            <Input value={form.intern_number} onChange={(e) => setForm({ ...form, intern_number: e.target.value })} placeholder="R1, R2..." data-testid="trailer-intern"/>
          </div>
          <div>
            <Label className="small-label">Marca</Label>
            <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} placeholder="Humbaur" data-testid="trailer-marca"/>
          </div>
          <div>
            <Label className="small-label">Model</Label>
            <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="HT 3000" data-testid="trailer-model"/>
          </div>
          <div>
            <Label className="small-label">An fabricație</Label>
            <Input type="number" min="1950" max="2099" value={form.an_fabricatie} onChange={(e) => setForm({ ...form, an_fabricatie: e.target.value })}/>
          </div>
          <div>
            <Label className="small-label">Culoare</Label>
            <Input value={form.culoare} onChange={(e) => setForm({ ...form, culoare: e.target.value })}/>
          </div>
          <div className="col-span-2">
            <Label className="small-label">VIN / Șasiu</Label>
            <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} placeholder="W09HT3000ABC1234" data-testid="trailer-vin"/>
          </div>
          <div>
            <Label className="small-label">Capacitate (kg)</Label>
            <Input type="number" min="0" step="10" value={form.capacitate_kg} onChange={(e) => setForm({ ...form, capacitate_kg: e.target.value })}/>
          </div>
          <div>
            <Label className="small-label">Atașează la autoturism</Label>
            <Select value={form.linked_car_id || "__none__"} onValueChange={(v) => setForm({ ...form, linked_car_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="trailer-link-car"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Niciunul —</SelectItem>
                {cars.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.numar_inmatriculare || c.marca || c.id.slice(0, 6)}
                    {c.marca ? ` · ${c.marca}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="small-label">Note</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="trailer-active-switch"/>
            <span className="text-sm">{form.active ? "Activă" : "Inactivă"}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="trailer-save-btn">
            {saving ? "Se salvează…" : (trailer ? "Salvează" : "Adaugă")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { TrailerFormDialog };
